import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ToastContainer, ToastProvider } from "@vms/ui-web";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { OrgProvider } from "./context/OrgContext";
import { Topbar } from "./components/layout/Topbar";
import { Sidebar } from "./components/layout/Sidebar";
import { FpzeroOnly, OrgGuard, VenueGuard } from "./components/layout/RouteGuards";
import { CommandPalette } from "./components/CommandPalette";

import { Login } from "./pages/Login";
import { HomeRedirect } from "./pages/HomeRedirect";
import { Organisations } from "./pages/admin/Organisations";
import { Releases } from "./pages/Releases";
import { Licences } from "./pages/Licences";
import { Audit } from "./pages/Audit";
import { AuditLog } from "./pages/admin/AuditLog";
import { SyncHealth } from "./pages/admin/SyncHealth";
import { Webhooks } from "./pages/org/Webhooks";

import { OrgDashboard } from "./pages/org/OrgDashboard";
import { DriverDetail } from "./pages/org/DriverDetail";
import { Members } from "./pages/org/Members";
import { OrgSettings } from "./pages/org/OrgSettings";
import { Templates } from "./pages/org/Templates";
import { OrgLeaderboards } from "./pages/org/OrgLeaderboards";
import { OrgLeaderboardForm } from "./pages/org/OrgLeaderboardForm";
import { Venues } from "./pages/Venues";
import { Drivers } from "./pages/Drivers";
import { Bookings } from "./pages/Bookings";
import { PartnerKeys } from "./pages/PartnerKeys";

import { VenueDashboard } from "./pages/venue/VenueDashboard";
import { VenueBookings } from "./pages/venue/VenueBookings";
import { VenueEvents } from "./pages/venue/VenueEvents";
import { VenueEventDetail } from "./pages/venue/VenueEventDetail";
import { VenueSessions } from "./pages/venue/VenueSessions";
import { VenueSessionDetail } from "./pages/venue/VenueSessionDetail";
import { VenueLapTimes } from "./pages/venue/VenueLapTimes";
import { VenueLeaderboards } from "./pages/venue/VenueLeaderboards";
import { VenueDrivers } from "./pages/venue/VenueDrivers";
import { VenueResults } from "./pages/venue/VenueResults";
import { VenueAudit } from "./pages/venue/VenueAudit";
import { VenueSettings } from "./pages/venue/VenueSettings";
import { LeaderboardDisplay } from "./pages/display/LeaderboardDisplay";
import { PublicLeaderboardDisplay } from "./pages/display/PublicLeaderboardDisplay";
import { HotlapDisplay } from "./pages/display/HotlapDisplay";
import { RaceDisplay } from "./pages/display/RaceDisplay";
import { VenueDisplay } from "./pages/display/VenueDisplay";
import { PublicEventLeaderboard } from "./pages/public/PublicEventLeaderboard";
import { PublicDriverResults } from "./pages/public/PublicDriverResults";

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <RoutedApp />
        <ToastContainer />
      </ToastProvider>
    </AuthProvider>
  );
}

function RoutedApp() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-surface" />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Token-gated public display URL. Display-agents load this in a
            webview after the operator pushes; no interactive login. The
            token in the query string is validated server-side. */}
        <Route path="/embed/leaderboard/:leaderboardId" element={<PublicLeaderboardDisplay />} />
        {/* Public, key-gated per-event leaderboard — no login. */}
        <Route path="/l/:eventKey" element={<PublicEventLeaderboard />} />
        {/* Public driver results (QR target) — cloud-first, offline payload fallback. */}
        <Route path="/r/:eventKey/:sessionId" element={<PublicDriverResults />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // OrgProvider must wrap every route so the topbar / sidebar can read the
  // current org/venue from URL params consistently.
  return (
    <Routes>
      {/* Chrome-less display routes — rendered outside ShellLayout so no
          sidebar/topbar; suitable for projecting on a venue TV. Still
          auth-required (the AuthProvider above bounces non-authed users to
          /login). Mirror of hub-ui's /display/* set; cloud version exists so
          operators / partners can pull up the same views remotely over WAN. */}
      <Route path="/display/hotlap/:eventId" element={<HotlapDisplay />} />
      <Route path="/display/race/:eventId" element={<RaceDisplay />} />
      <Route path="/display/venue/:venueId" element={<VenueDisplay />} />
      <Route
        path="/display/leaderboard/:leaderboardId"
        element={<LeaderboardDisplay />}
      />
      {/* Same public-token-gated route, mirrored into the authed tree so
          an operator can preview a pushed URL without logging out. */}
      <Route
        path="/embed/leaderboard/:leaderboardId"
        element={<PublicLeaderboardDisplay />}
      />
      {/* Public, key-gated per-event leaderboard, mirrored into the authed tree
          so an operator can preview the link without logging out. */}
      <Route path="/l/:eventKey" element={<PublicEventLeaderboard />} />
      <Route path="/r/:eventKey/:sessionId" element={<PublicDriverResults />} />

      <Route element={<ShellLayout />}>
        {/* Persona-aware home */}
        <Route path="/" element={<HomeRedirect />} />

        {/* FPZERO admin sections — global, not org-scoped */}
        <Route path="/admin" element={<FpzeroOnly />}>
          <Route path="organisations" element={<Organisations />} />
          <Route path="releases" element={<Releases />} />
          <Route path="licences" element={<Licences />} />
          <Route path="sync" element={<SyncHealth />} />
          <Route path="audit" element={<AuditLog />} />
          <Route index element={<Navigate to="organisations" replace />} />
        </Route>

        {/* Org-scoped routes — `:slug` resolves to the active organisation */}
        <Route path="/org/:slug" element={<OrgGuard />}>
          <Route index element={<OrgDashboard />} />
          <Route path="venues" element={<Venues />} />
          <Route path="drivers" element={<Drivers />} />
          <Route path="drivers/:driverId" element={<DriverDetail />} />
          <Route path="bookings" element={<Bookings />} />
          <Route path="leaderboards" element={<OrgLeaderboards />} />
          <Route path="leaderboards/new" element={<OrgLeaderboardForm />} />
          <Route path="leaderboards/:id/edit" element={<OrgLeaderboardForm />} />
          <Route path="templates/branding" element={<Templates kind="branding" />} />
          <Route path="templates/race" element={<Templates kind="race" />} />
          <Route path="templates/difficulty" element={<Templates kind="difficulty" />} />
          <Route path="partners" element={<PartnerKeys />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="members" element={<Members />} />
          <Route path="settings" element={<OrgSettings />} />

          {/* Venue-scoped routes — `:vslug` resolves to the active venue */}
          <Route path="venue/:vslug" element={<VenueGuard />}>
            <Route index element={<VenueDashboard />} />
            <Route path="events" element={<VenueEvents />} />
            <Route path="events/:eventId" element={<VenueEventDetail />} />
            <Route path="sessions" element={<VenueSessions />} />
            <Route path="sessions/:sessionId" element={<VenueSessionDetail />} />
            <Route path="lap-times" element={<VenueLapTimes />} />
            <Route path="results" element={<VenueResults />} />
            <Route path="leaderboards" element={<VenueLeaderboards />} />
            <Route path="bookings" element={<VenueBookings />} />
            <Route path="drivers" element={<VenueDrivers />} />
            <Route path="audit" element={<VenueAudit />} />
            <Route path="settings" element={<VenueSettings />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Shared chrome — topbar + sidebar + main content. Sidebar/topbar read the
 *  org context, so OrgProvider must wrap them. */
function ShellLayout() {
  return (
    <OrgProvider>
      <div className="h-screen flex bg-surface overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <Topbar />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl">
              <Outlet />
            </div>
          </main>
        </div>
        <CommandPalette />
      </div>
    </OrgProvider>
  );
}
